import React, { useState, useEffect, useReducer, useContext, useMemo } from "react";
import { toast } from "react-toastify";
import { useHistory } from "react-router-dom";

import { makeStyles, useTheme, alpha } from "@material-ui/core/styles";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import {
  Paper,
  Button,
  TextField,
  InputAdornment,
  Typography,
  Chip,
  Tooltip,
  CircularProgress,
} from "@material-ui/core";
import SearchIcon from "@material-ui/icons/Search";
import AddIcon from "@material-ui/icons/Add";
import DeviceHubIcon from "@material-ui/icons/DeviceHub";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import ErrorOutlineIcon from "@material-ui/icons/ErrorOutline";

import MainContainer from "../../components/MainContainer";
import Title from "../../components/Title";
import IntegrationModal from "../../components/QueueIntegrationModal";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { socketConnection } from "../../services/socket";
import { AuthContext } from "../../context/Auth/AuthContext";
import usePlans from "../../hooks/usePlans";
import { financeiroStyles, toneColor } from "../Financeiro/financeiroShared";
import sidebarScrollbar from "../../helpers/sidebarScrollbar";
import IntegrationDeleteDialog from "./IntegrationDeleteDialog";
import { typeInfo, shortUrl } from "./integrationTypes";

// ---------------------------------------------------------------------------
// Reducer
// ---------------------------------------------------------------------------
// Eventos do socket não trazem as filas: mantém as que já estavam na tela.
const merge = (prev, next) => ({ ...next, queues: next.queues || (prev && prev.queues) || [] });

const reducer = (state, action) => {
  switch (action.type) {
    case "LOAD": {
      const next = [...state];
      action.payload.forEach((item) => {
        const idx = next.findIndex((i) => i.id === item.id);
        if (idx !== -1) next[idx] = merge(next[idx], item);
        else next.push(merge(null, item));
      });
      return next;
    }
    case "UPSERT": {
      const idx = state.findIndex((i) => i.id === action.payload.id);
      if (idx === -1) return [merge(null, action.payload), ...state];
      const next = [...state];
      next[idx] = merge(state[idx], action.payload);
      return next;
    }
    case "DELETE":
      return state.filter((i) => i.id !== action.payload);
    case "RESET":
      return [];
    default:
      return state;
  }
};

const FILTERS = [
  ["all", "Todas"],
  ["typebot", "Typebot"],
  ["hook", "n8n / Webhook"],
  ["noqueue", "Sem fila"],
];

const matchesFilter = (item, filter) => {
  if (filter === "typebot") return item.type === "typebot";
  if (filter === "hook") return item.type === "n8n" || item.type === "webhook";
  if (filter === "noqueue") return !item.queues || item.queues.length === 0;
  return true;
};

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------
const useStyles = makeStyles((theme) => ({
  ...financeiroStyles(theme),
  header: { ...financeiroStyles(theme).header, flexShrink: 0 },
  grid: {
    flex: 1,
    overflowY: "auto",
    padding: theme.spacing(2, 2.5),
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
    alignContent: "start",
    gap: theme.spacing(1.5),
    ...sidebarScrollbar(theme),
    [theme.breakpoints.down("xs")]: { padding: theme.spacing(1.5), gridTemplateColumns: "1fr" },
  },
  card: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 12,
    padding: theme.spacing(1.75),
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1.25),
    minWidth: 0,
    backgroundColor: theme.palette.background.paper,
  },
  cardHead: { display: "flex", alignItems: "center", gap: theme.spacing(1.25), minWidth: 0 },
  badge: {
    width: 38,
    height: 38,
    borderRadius: 9,
    flexShrink: 0,
    display: "grid",
    placeItems: "center",
    color: "#fff",
    fontWeight: 700,
    fontSize: 12,
  },
  name: {
    fontWeight: 600,
    lineHeight: 1.25,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  role: { fontSize: 12, color: theme.palette.text.secondary },
  url: {
    fontFamily: "ui-monospace, Menlo, Consolas, monospace",
    fontSize: 12,
    color: theme.palette.text.secondary,
    backgroundColor: theme.palette.type === "dark" ? alpha("#fff", 0.05) : alpha("#000", 0.04),
    borderRadius: 6,
    padding: theme.spacing(0.5, 1),
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  queues: { display: "flex", flexWrap: "wrap", gap: 6 },
  queueChip: { height: 22, fontSize: 12, fontWeight: 600, borderRadius: 999 },
  legacyNote: {
    fontSize: 12.5,
    lineHeight: 1.45,
    padding: theme.spacing(1, 1.25),
    borderRadius: 8,
    color: theme.palette.text.primary,
    backgroundColor: alpha(toneColor(theme, "amber"), 0.12),
  },
  cardFoot: {
    marginTop: "auto",
    borderTop: `1px solid ${theme.palette.divider}`,
    paddingTop: theme.spacing(1),
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(0.75),
  },
  cardActions: { display: "flex", justifyContent: "flex-end", gap: theme.spacing(0.75), flexWrap: "wrap" },
  smallButton: { textTransform: "none", fontWeight: 600, borderRadius: 8, minWidth: 0 },
  dangerButton: { color: theme.palette.error.main },
  testResult: { display: "flex", alignItems: "center", gap: 6, fontSize: 12.5, lineHeight: 1.35 },
  ok: { color: toneColor(theme, "green") },
  fail: { color: toneColor(theme, "red") },
  empty: {
    gridColumn: "1 / -1",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    textAlign: "center",
    padding: theme.spacing(6, 2),
    color: theme.palette.text.secondary,
    gap: theme.spacing(1),
  },
  emptyIcon: { fontSize: 48, opacity: 0.35 },
}));

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
const QueueIntegration = () => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("xs"));
  const history = useHistory();
  const { user } = useContext(AuthContext);
  const { getPlanCompany } = usePlans();

  const [items, dispatch] = useReducer(reducer, []);
  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [filter, setFilter] = useState("all");
  const [modal, setModal] = useState({ open: false, id: null });
  const [deleting, setDeleting] = useState(null);
  const [tests, setTests] = useState({});

  useEffect(() => {
    (async () => {
      const planConfigs = await getPlanCompany(undefined, user.companyId);
      if (!planConfigs.plan.useIntegrations) {
        toast.error("Esta empresa não possui permissão para acessar essa página! Estamos lhe redirecionando.");
        setTimeout(() => history.push("/"), 1000);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam]);

  useEffect(() => {
    setLoading(true);
    const timer = setTimeout(async () => {
      try {
        const { data } = await api.get("/queueIntegration/", { params: { searchParam, pageNumber } });
        dispatch({ type: "LOAD", payload: data.queueIntegrations });
        setHasMore(data.hasMore);
      } catch (err) {
        toastError(err);
      } finally {
        setLoading(false);
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [searchParam, pageNumber]);

  useEffect(() => {
    const socket = socketConnection({ companyId: user.companyId });
    socket.on(`company-${user.companyId}-queueIntegration`, (data) => {
      if (data.action === "update" || data.action === "create") {
        dispatch({ type: "UPSERT", payload: data.queueIntegration });
      }
      if (data.action === "delete") {
        dispatch({ type: "DELETE", payload: +data.integrationId });
      }
    });
    return () => socket.disconnect();
  }, [user]);

  const visible = useMemo(() => items.filter((i) => matchesFilter(i, filter)), [items, filter]);
  const countOf = (key) => items.filter((i) => matchesFilter(i, key)).length;

  const handleScroll = (e) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) setPageNumber((p) => p + 1);
  };

  const handleTest = async (item) => {
    setTests((t) => ({ ...t, [item.id]: { loading: true } }));
    try {
      const { data } = await api.post("/queueIntegration/test", {
        type: item.type,
        urlN8N: item.urlN8N,
        typebotSlug: item.typebotSlug,
      });
      setTests((t) => ({ ...t, [item.id]: { result: data } }));
    } catch (err) {
      setTests((t) => ({ ...t, [item.id]: null }));
      toastError(err);
    }
  };

  const renderTest = (item) => {
    const state = tests[item.id];
    if (!state) return null;
    if (state.loading) {
      return (
        <div className={classes.testResult}>
          <CircularProgress size={12} /> Testando…
        </div>
      );
    }
    const { ok, message } = state.result;
    return (
      <div className={`${classes.testResult} ${ok ? classes.ok : classes.fail}`} role="status">
        {ok ? <CheckCircleIcon style={{ fontSize: 15 }} /> : <ErrorOutlineIcon style={{ fontSize: 15 }} />}
        <span>{message}</span>
      </div>
    );
  };

  const renderCard = (item) => {
    const info = typeInfo(item.type);
    const queues = item.queues || [];
    return (
      <div key={item.id} className={classes.card}>
        <div className={classes.cardHead}>
          <span className={classes.badge} style={{ backgroundColor: info.color }} aria-hidden="true">
            {info.short}
          </span>
          <div style={{ minWidth: 0 }}>
            <div className={classes.name} title={item.name}>
              {item.name}
            </div>
            <div className={classes.role}>
              {info.label}
              {info.role ? ` · ${info.role}` : ""}
            </div>
          </div>
        </div>

        {info.legacy ? (
          <div className={classes.legacyNote}>
            Este tipo não é mais suportado e não responde mensagens. Pode ser excluído.
          </div>
        ) : (
          item.urlN8N && (
            <div className={classes.url} title={shortUrl(item.urlN8N)}>
              {shortUrl(item.urlN8N)}
            </div>
          )
        )}

        <div className={classes.queues}>
          {queues.length === 0 ? (
            <Chip
              size="small"
              label={info.legacy ? "Sem fila" : "Não está em nenhuma fila"}
              className={`${classes.queueChip} ${info.legacy ? classes.pillGrey : classes.pillAmber}`}
            />
          ) : (
            queues.map((q) => (
              <Chip
                key={q.id}
                size="small"
                label={q.name}
                className={classes.queueChip}
                style={{
                  color: q.color || theme.palette.primary.main,
                  backgroundColor: alpha(q.color || theme.palette.primary.main, 0.14),
                }}
              />
            ))
          )}
        </div>

        <div className={classes.cardFoot}>
          {renderTest(item)}
          <div className={classes.cardActions}>
            {!info.legacy && (
              <Tooltip title={item.type === "typebot" ? "Inicia uma conversa de teste no bot" : "Envia um evento de teste para a URL"}>
                <span>
                  <Button
                    size="small"
                    className={classes.smallButton}
                    disabled={Boolean(tests[item.id] && tests[item.id].loading)}
                    onClick={() => handleTest(item)}
                  >
                    Testar
                  </Button>
                </span>
              </Tooltip>
            )}
            {!info.legacy && (
              <Button
                size="small"
                color="primary"
                className={classes.smallButton}
                onClick={() => setModal({ open: true, id: item.id })}
              >
                Editar
              </Button>
            )}
            <Button
              size="small"
              className={`${classes.smallButton} ${classes.dangerButton}`}
              onClick={() => setDeleting(item)}
            >
              Excluir
            </Button>
          </div>
        </div>
      </div>
    );
  };

  const emptyText = searchParam
    ? `Nenhuma integração encontrada para "${searchParam}".`
    : filter !== "all"
    ? "Nenhuma integração neste filtro."
    : "Nenhuma integração cadastrada ainda.";

  return (
    <MainContainer>
      <IntegrationModal
        open={modal.open}
        integrationId={modal.id}
        onClose={() => setModal({ open: false, id: null })}
      />
      <IntegrationDeleteDialog
        integration={deleting}
        onClose={() => setDeleting(null)}
        onDeleted={(id) => {
          dispatch({ type: "DELETE", payload: id });
          setDeleting(null);
        }}
      />

      <div className={classes.header}>
        <div className={classes.headerTitle}>
          <Title>Integrações</Title>
          <Typography variant="body2" className={classes.muted}>
            Robôs e sistemas externos ligados às suas filas.
          </Typography>
        </div>
        <div className={classes.headerButtons}>
          <Button
            variant="contained"
            color="primary"
            disableElevation
            className={classes.headerButton}
            startIcon={<AddIcon />}
            onClick={() => setModal({ open: true, id: null })}
          >
            {isMobile ? "Nova" : "Nova integração"}
          </Button>
        </div>
      </div>

      <Paper className={classes.mainPaper} variant="outlined">
        <div className={classes.toolbar} style={isMobile ? { padding: theme.spacing(1.5) } : undefined}>
          <TextField
            className={classes.searchField}
            placeholder="Buscar integração"
            type="search"
            variant="outlined"
            size="small"
            value={searchParam}
            onChange={(e) => setSearchParam(e.target.value.toLowerCase())}
            inputProps={{ "aria-label": "Buscar integração" }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon style={{ color: theme.palette.text.secondary }} />
                </InputAdornment>
              ),
            }}
          />
          <div
            className={`${classes.chipGroup} ${isMobile ? classes.chipGroupScroll : ""}`}
            role="group"
            aria-label="Filtrar integrações"
          >
            {FILTERS.map(([key, label]) => (
              <Chip
                key={key}
                clickable
                className={classes.filterChip}
                label={`${label} (${countOf(key)})`}
                color={filter === key ? "primary" : "default"}
                variant={filter === key ? "default" : "outlined"}
                aria-pressed={filter === key}
                onClick={() => setFilter(key)}
              />
            ))}
          </div>
        </div>

        <div className={classes.grid} onScroll={handleScroll}>
          {visible.map(renderCard)}

          {!loading && visible.length === 0 && (
            <div className={classes.empty}>
              <DeviceHubIcon className={classes.emptyIcon} />
              <Typography variant="body2">{emptyText}</Typography>
              {!searchParam && filter === "all" && (
                <>
                  <Typography variant="body2" style={{ maxWidth: 420 }}>
                    Conecte um Typebot para responder clientes automaticamente, ou envie as mensagens
                    para o n8n, Make ou outro sistema.
                  </Typography>
                  <Button
                    size="small"
                    color="primary"
                    variant="outlined"
                    className={classes.smallButton}
                    onClick={() => setModal({ open: true, id: null })}
                    style={{ marginTop: 8 }}
                  >
                    Criar primeira integração
                  </Button>
                </>
              )}
            </div>
          )}

          {loading && (
            <div className={classes.empty} style={{ padding: theme.spacing(3) }}>
              <CircularProgress size={24} />
            </div>
          )}
        </div>
      </Paper>
    </MainContainer>
  );
};

export default QueueIntegration;
